/**
 * PortProfiles — Switch > Switch Port Profiles (catalog kind "port_profile"; label disambiguated
 * from Wireless > AP Port Profiles in OP#1858).
 *
 * Reusable switch-port configs: access|trunk, a native VLAN, tagged VLANs, PoE, and
 * speed. References VLANs (catalog kind "network") BY ID. CRUD over
 * /api/v2/catalog/port_profile; VLAN options come from useCatalog("network").
 */
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useCatalog, useSaveCatalogObject, useDeleteCatalogObject, type CatalogObject } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";

const INPUT = "rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand";

export default function PortProfiles() {
  const { data, isLoading, error } = useCatalog("port_profile");
  const networks = useCatalog("network");
  const [editing, setEditing] = useState<CatalogObject | null>(null);
  const [creating, setCreating] = useState(false);
  const rows = data?.items ?? [];
  const vlanName = (id: unknown) =>
    (networks.data?.items ?? []).find((n) => n.id === id)?.name ?? (id != null ? `#${id}` : "—");

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <PageHeader title="Switch Port Profiles" subtitle="Reusable switch-port configs — access/trunk, VLANs, PoE, speed" />
        <RoleGate min="org_admin" mode="hide">
          <Button variant="primary" onClick={() => setCreating(true)}>Add profile</Button>
        </RoleGate>
      </div>

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading port profiles…</div>}

      {data && rows.length === 0 && (
        <EmptyState
          title="No port profiles yet"
          hint="Create a profile (e.g. AP-Uplink: trunk, native Mgmt, tagged Corp+Guest, PoE on) and apply it to switch ports."
        />
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-ink-subtle">
              <tr className="[&>th]:py-1.5 [&>th]:pr-4 [&>th]:font-medium">
                <th>Name</th><th>Mode</th><th>Native VLAN</th><th>Tagged</th><th>PoE</th><th>Speed</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-line/60 [&>td]:py-2 [&>td]:pr-4">
                  <td className="font-medium text-ink">{p.name}</td>
                  <td className="text-ink-muted">{String(p.mode)}</td>
                  <td className="text-ink-muted">{vlanName(p.native_network_id)}</td>
                  <td className="text-ink-muted">
                    {((p.tagged_network_ids as number[]) ?? []).map(vlanName).join(", ") || "—"}
                  </td>
                  <td className="text-ink-muted">{p.poe_enabled ? "on" : "off"}</td>
                  <td className="font-mono text-ink-muted">{(p.speed as string) || "auto"}</td>
                  <td className="text-right">
                    <RoleGate min="org_admin" mode="hide">
                      <Button variant="secondary" onClick={() => setEditing(p)}>Edit</Button>
                    </RoleGate>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <ProfileDialog networks={networks.data?.items ?? []} onClose={() => setCreating(false)} />}
      {editing && <ProfileDialog networks={networks.data?.items ?? []} obj={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ProfileDialog({ obj, networks, onClose }: { obj?: CatalogObject; networks: CatalogObject[]; onClose: () => void }) {
  const save = useSaveCatalogObject("port_profile");
  const del = useDeleteCatalogObject("port_profile");
  const editing = !!obj;

  const [name, setName] = useState(obj?.name ?? "");
  const [mode, setMode] = useState<string>((obj?.mode as string) ?? "access");
  const [nativeId, setNativeId] = useState<string>(obj?.native_network_id != null ? String(obj.native_network_id) : "");
  const [tagged, setTagged] = useState<number[]>(((obj?.tagged_network_ids as number[]) ?? []).slice());
  const [poe, setPoe] = useState(obj?.poe_enabled !== false);
  const [speed, setSpeed] = useState<string>((obj?.speed as string) ?? "auto");

  const toggleTag = (id: number) =>
    setTagged((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    save.mutate(
      {
        ...(editing ? { id: obj!.id } : {}),
        name: name.trim(),
        mode,
        native_network_id: nativeId ? Number(nativeId) : null,
        tagged_network_ids: mode === "trunk" ? tagged : [],
        poe_enabled: poe,
        speed,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet max-h-[85vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">
            {editing ? `Edit ${obj!.name}` : "Add port profile"}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Access ports carry one untagged VLAN; trunk ports carry a native VLAN plus tagged VLANs.
          </Dialog.Description>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="AP-Uplink" className={INPUT} />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Mode</span>
                <select value={mode} onChange={(e) => setMode(e.target.value)} className={INPUT}>
                  <option value="access">access</option>
                  <option value="trunk">trunk</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">{mode === "trunk" ? "Native VLAN" : "VLAN"}</span>
                <select value={nativeId} onChange={(e) => setNativeId(e.target.value)} className={INPUT}>
                  <option value="">— none —</option>
                  {networks.map((n) => (
                    <option key={n.id} value={n.id}>{n.name}{n.vlan_id != null ? ` (${n.vlan_id})` : ""}</option>
                  ))}
                </select>
              </label>
            </div>

            {mode === "trunk" && (
              <div className="rounded border border-line bg-surface-2 p-3">
                <div className="mb-2 text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">Tagged VLANs</div>
                {networks.length === 0 && <div className="text-xs text-ink-muted">No VLANs defined yet.</div>}
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {networks.map((n) => (
                    <label key={n.id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={tagged.includes(n.id)} onChange={() => toggleTag(n.id)} />
                      <span className="text-ink-muted">{n.name}{n.vlan_id != null ? ` (${n.vlan_id})` : ""}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2 py-2 text-sm">
                <input type="checkbox" checked={poe} onChange={(e) => setPoe(e.target.checked)} />
                <span className="text-ink-muted">PoE enabled</span>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Speed</span>
                <select value={speed} onChange={(e) => setSpeed(e.target.value)} className={INPUT}>
                  <option value="auto">auto</option><option value="100">100M</option>
                  <option value="1000">1G</option><option value="2500">2.5G</option><option value="10000">10G</option>
                </select>
              </label>
            </div>

            {save.isError && <span className="text-xs text-critical">Could not save the profile.</span>}
            <div className="md-dialog-footer flex items-center justify-between gap-2">
              {editing ? (
                <RoleGate min="org_admin" mode="hide">
                  <Button variant="danger" type="button" disabled={del.isPending}
                    onClick={() => del.mutate(obj!.id, { onSuccess: onClose })}>
                    {del.isPending ? "…" : "Delete"}
                  </Button>
                </RoleGate>
              ) : <span />}
              <div className="flex items-center gap-2">
                <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
                <Button variant="primary" type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
